import React from 'react';

/**
 * Generates a unique id prefixed for components aria attributes.
 *
 * Will use React 18 `useId` if available, otherwise generate a randomized number based on
 * the timestamp.
 *
 * Automatic id generation can be overwritten by consumers that provide a component id.
 *
 * @param prefix The id prefix
 * @param suffix Optionally replace the automatically generated id
 * @returns `{prefix}-{random-id|manual-suffix}`
 *
 * @since 2.4.0
 * @private This hook is private, users should use `useId` from React 18 or provide their own ids.
 */
function useComponentId(prefix, suffix) {
  if (process.env.NODE_ENV === 'test') {
    return "".concat(prefix, "-").concat(suffix || 'useComponentId-TEST');
  }
  // If consumer uses React < 18 they don't have `useId`. So we want to explicitly walk around TS.
  // @ts-ignore
  const {
    useId: reactUseId
  } = React;
  // if useId exists, use it, otherwise generate a timestamp and randomize it
  const componentId = typeof reactUseId === 'function' ? reactUseId() : Math.floor(Date.now() * Math.random());
  const [suffixVal] = React.useState(suffix !== null && suffix !== void 0 ? suffix : componentId);
  return "".concat(prefix, "-").concat(suffixVal);
}

export { useComponentId };
